import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Layout, message } from 'antd';
import { mockPosts, mockUsers } from './api/mockData';
import './App.css';

// 导入组件和页面
import Header from './components/Header';
import Footer from './components/FooterItem';
import Home from './pages/Home';
import About from './pages/About';
import Archive from './pages/Archive';
import PostDetail from './pages/PostDetail';
import WritePost from './pages/WritePost';
import Auth from './pages/Auth';
import NotFound from './pages/NotFound';

const { Content } = Layout;

// 滚动到顶部的组件
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    // 每次路由变化时立即滚动到顶部
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// 加载组件
const LoadingPage = () => {
  return (
    <div className="app-loading">
      <div className="loading-content">
        <h1>我的博客</h1>
        <p>加载中...</p>
      </div>
    </div>
  );
};

// 完整用户数据结构
const getCompleteUserData = (userData) => {
  return {
    id: userData.id,
    username: userData.username,
    email: userData.email,
    avatar: userData.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${userData.username}`,
    bio: userData.bio || '这个人很神秘，什么也没留下。',
    techStack: userData.techStack || [],
    interests: userData.interests || [],
    github: userData.github || '',
    twitter: userData.twitter || '',
    website: userData.website || '',
    registerDate: userData.registerDate || userData.createdAt || new Date().toISOString().split('T')[0],
    // 保留密码等敏感信息但不暴露给前端
    role: userData.role || 'user'
  };
};

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [posts, setPosts] = useState([]);

  // 初始化数据
  useEffect(() => {
    const initData = async () => {
      try {
        console.log('开始初始化数据...');
        
        // 1. 检查是否有已登录用户
        const savedUser = localStorage.getItem('blog_user');
        if (savedUser) {
          try {
            const parsedUser = JSON.parse(savedUser);
            if (parsedUser && parsedUser.username) {
              const completeUser = getCompleteUserData(parsedUser);
              console.log('加载已登录用户:', completeUser);
              setUser(completeUser);
            }
          } catch (error) {
            console.error('解析用户数据失败:', error);
            localStorage.removeItem('blog_user');
          }
        }

        // 2. 初始化用户数据（如果还没有用户数据）
        const savedUsers = localStorage.getItem('blog_users');
        if (!savedUsers) {
          console.log('初始化用户数据...');
          // 确保 mockUsers 有完整的字段
          const completeMockUsers = mockUsers.map(user => ({
            ...user,
            techStack: user.techStack || [],
            interests: user.interests || [],
            github: user.github || '',
            twitter: user.twitter || '',
            website: user.website || '',
            bio: user.bio || '这个人很神秘，什么也没留下。'
          }));
          localStorage.setItem('blog_users', JSON.stringify(completeMockUsers));
          console.log('初始化用户数据完成:', completeMockUsers);
        } else {
          // 更新已有用户的字段结构
          try {
            const existingUsers = JSON.parse(savedUsers);
            const updatedUsers = existingUsers.map(user => ({
              ...user,
              techStack: user.techStack || [],
              interests: user.interests || [],
              github: user.github || '',
              twitter: user.twitter || '',
              website: user.website || '',
              bio: user.bio || '这个人很神秘，什么也没留下。'
            }));
            localStorage.setItem('blog_users', JSON.stringify(updatedUsers));
          } catch (error) {
            console.error('更新用户数据结构失败:', error);
          }
        }

        // 3. 初始化文章数据
        const savedPosts = localStorage.getItem('blog_posts');
        if (!savedPosts) {
          console.log('初始化文章数据...');
          localStorage.setItem('blog_posts', JSON.stringify(mockPosts));
          setPosts(mockPosts);
          console.log('初始化文章数据完成:', mockPosts.length);
        } else {
          try {
            const parsedPosts = JSON.parse(savedPosts);
            setPosts(parsedPosts);
            console.log('加载现有文章数据:', parsedPosts.length);
          } catch (error) {
            console.error('解析文章数据失败:', error);
            localStorage.setItem('blog_posts', JSON.stringify(mockPosts));
            setPosts(mockPosts);
          }
        }

        // 4. 初始化点赞记录
        if (!localStorage.getItem('liked_posts')) {
          localStorage.setItem('liked_posts', JSON.stringify([]));
        }
        
        console.log('数据初始化完成');
      } catch (error) {
        console.error('初始化数据失败:', error);
        message.error('数据初始化失败，请刷新页面重试');
      } finally {
        // 模拟加载延迟
        setTimeout(() => {
          setIsLoading(false);
        }, 300);
      }
    };

    initData();
  }, []);

  // 用户登录处理
  const handleLogin = (userData) => {
    console.log('开始登录处理，传入数据:', userData);
    
    // 确保用户数据完整
    const completeUserData = getCompleteUserData(userData);
    console.log('完整用户数据:', completeUserData);
    
    // 保存到 state
    setUser(completeUserData);
    
    // 保存到 localStorage
    localStorage.setItem('blog_user', JSON.stringify(completeUserData));
    
    console.log('登录处理完成');
    return true;
  };

  // 用户登出
  const handleLogout = () => {
    try {
      console.log('用户登出:', user?.username);
      setUser(null);
      localStorage.removeItem('blog_user');
      message.success('已退出登录');
      console.log('登出完成');
    } catch (error) {
      console.error('退出登录失败:', error);
      message.error('退出登录失败');
    }
  };

  // 创建文章
  const handleCreatePost = (postData) => {
    try {
      console.log('创建文章:', postData);
      
      const newPost = {
        ...postData,
        id: Date.now(),
        author: user?.username || '匿名',
        date: new Date().toISOString().split('T')[0],
        views: 0,
        comments: 0,
        likes: 0,
        isPublished: true,
        image: postData.image || `https://picsum.photos/600/400?random=${Date.now()}`
      };
      
      const updatedPosts = [newPost, ...posts];
      setPosts(updatedPosts);
      localStorage.setItem('blog_posts', JSON.stringify(updatedPosts));
      message.success('文章发布成功！');
      
      console.log('文章创建成功:', newPost);
      return newPost;
    } catch (error) {
      console.error('创建文章失败:', error);
      message.error('文章发布失败');
      throw error;
    }
  };

  // 更新文章
  const handleUpdatePost = (id, postData) => {
    try {
      const postId = parseInt(id);
      console.log('更新文章 ID:', postId, '数据:', postData);
      
      const updatedPosts = posts.map(post => 
        post.id === postId 
          ? { ...post, ...postData, updatedAt: new Date().toISOString() }
          : post
      );
      setPosts(updatedPosts);
      localStorage.setItem('blog_posts', JSON.stringify(updatedPosts));
      message.success('文章更新成功！');
      
      const updatedPost = updatedPosts.find(p => p.id === postId);
      console.log('文章更新成功:', updatedPost);
      return updatedPost;
    } catch (error) {
      console.error('更新文章失败:', error);
      message.error('文章更新失败');
      throw error;
    }
  };

  // 删除文章
  const handleDeletePost = (id) => {
    try {
      const postId = parseInt(id);
      console.log('删除文章 ID:', postId);
      
      const updatedPosts = posts.filter(post => post.id !== postId);
      setPosts(updatedPosts);
      localStorage.setItem('blog_posts', JSON.stringify(updatedPosts));
      message.success('文章删除成功！');
      
      console.log('文章删除成功，剩余文章数:', updatedPosts.length);
    } catch (error) {
      console.error('删除文章失败:', error);
      message.error('文章删除失败');
    }
  };

  // 更新用户信息
  const handleUpdateUser = (userData) => {
    try {
      if (!user) {
        message.error('请先登录');
        return;
      }

      console.log('更新用户信息:', userData);
      
      // 更新全局用户信息
      const updatedUser = { 
        ...user,
        bio: userData.bio || user.bio || '这个人很神秘，什么也没留下。',
        techStack: userData.techStack || user.techStack || [],
        interests: userData.interests || user.interests || [],
        github: userData.github || user.github || '',
        twitter: userData.twitter || user.twitter || '',
        website: userData.website || user.website || '',
        avatar: userData.avatar || user.avatar
      };
      
      console.log('更新后的用户:', updatedUser);
      
      // 更新 state
      setUser(updatedUser);
      
      // 保存到localStorage
      localStorage.setItem('blog_user', JSON.stringify(updatedUser));
      
      // 同时更新用户列表中的信息
      const allUsers = JSON.parse(localStorage.getItem('blog_users') || '[]');
      const userIndex = allUsers.findIndex(u => u.username === user.username);
      
      if (userIndex !== -1) {
        const userToUpdate = allUsers[userIndex];
        allUsers[userIndex] = { 
          ...userToUpdate, 
          bio: updatedUser.bio,
          techStack: updatedUser.techStack,
          interests: updatedUser.interests,
          github: updatedUser.github,
          twitter: updatedUser.twitter,
          website: updatedUser.website,
          avatar: updatedUser.avatar
        };
        localStorage.setItem('blog_users', JSON.stringify(allUsers));
        console.log('更新用户列表成功');
      }
      
      message.success('个人信息更新成功！');
    } catch (error) {
      console.error('更新用户信息失败:', error);
      message.error('更新失败，请重试！');
    }
  };

  // 如果是加载中，显示加载页面
  if (isLoading) {
    return <LoadingPage />;
  }

  return (
    <Router>
      <ScrollToTop />
      <Layout className="layout">
            <div className="sakura-pattern"></div>
      
      {/* 装饰性樱花元素 */}
      <div className="decoration-sakura top-left">❀</div>
      <div className="decoration-sakura bottom-right">❀</div>
      <div className="decoration-sakura center">✿</div>
        {/* 如果用户未登录，不显示Header */}
        {user && <Header user={user} onLogout={handleLogout} />}
        
        <Content className="site-layout-content">
          <Routes>
            {/* 首页：已登录用户才能访问 */}
            <Route 
              path="/" 
              element={
                user ? <Home posts={posts} /> : <Navigate to="/auth" replace />
              } 
            />
            
            {/* 登录注册页面：未登录用户才能访问 */}
            <Route 
              path="/auth" 
              element={
                user ? <Navigate to="/" replace /> : <Auth onLogin={handleLogin} />
              } 
            />
            
            {/* 写文章页面 - 需要登录 */}
            <Route 
              path="/write" 
              element={
                user ? (
                  <WritePost 
                    user={user} 
                    onCreatePost={handleCreatePost}
                    posts={posts}
                  />
                ) : (
                  <Navigate to="/auth" replace />
                )
              } 
            />
            
            <Route 
              path="/write/:id" 
              element={
                user ? (
                  <WritePost 
                    user={user} 
                    onCreatePost={handleCreatePost}
                    onUpdatePost={handleUpdatePost}
                    posts={posts}
                  />
                ) : (
                  <Navigate to="/auth" replace />
                )
              } 
            />
            
            {/* 关于我页面 - 需要登录 */}
            <Route 
              path="/about" 
              element={
                user ? (
                  <About 
                    user={user} 
                    onUpdateUser={handleUpdateUser}
                    posts={posts}
                  />
                ) : (
                  <Navigate to="/auth" replace />
                )
              } 
            />
            
            {/* 归档页面 - 公开访问 */}
            <Route path="/archive" element={<Archive posts={posts} />} />
            
            {/* 文章详情页面 - 公开访问 */}
            <Route 
              path="/post/:id" 
              element={
                <PostDetail 
                  posts={posts} 
                  onUpdatePost={handleUpdatePost}
                  onDeletePost={handleDeletePost}
                  user={user}
                />
              } 
            />
            
            {/* 404页面 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Content>
        
        {/* 如果用户未登录，不显示Footer */}
        {user && <Footer />}
      </Layout>
    </Router>
  );
}

export default App;